import { normalizeEmail } from '@open-dataroom/shared';
import { createFileRoute } from '@tanstack/react-router';
import { Mail, ArrowRight, CheckCircle } from 'lucide-react';
import * as React from 'react';

import { Button } from '~/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '~/components/ui/card';
import { Input } from '~/components/ui/input';
import { useAppSettings } from '~/hooks/use-app-settings';
import {
  formatDocumentTitle,
  useDocumentTitle
} from '~/hooks/use-document-title';
import { api } from '~/lib/api';
import { redirectAuthenticatedToDashboard } from '~/lib/route-auth';

export const Route = createFileRoute('/login')({
  component: LoginPage,
  beforeLoad: async () => {
    await redirectAuthenticatedToDashboard();
  }
});

function LoginPage() {
  const [email, setEmail] = React.useState('');
  const [status, setStatus] = React.useState<
    'idle' | 'loading' | 'sent' | 'error'
  >('idle');
  const [error, setError] = React.useState('');

  const { data: settings } = useAppSettings();
  const companyName = settings?.companyName ?? 'Open Dataroom';

  useDocumentTitle(formatDocumentTitle('Sign in', companyName));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    setError('');
    try {
      await api.post('/api/auth/magic-link', {
        email: normalizeEmail(email),
        appOrigin: window.location.origin
      });
      setStatus('sent');
    } catch (err) {
      setStatus('error');
      setError(
        err instanceof Error ? err.message : 'Failed to send magic link'
      );
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Mail className="h-6 w-6" />
          </div>
          <CardTitle className="text-2xl">Welcome to {companyName}</CardTitle>
          <CardDescription>
            Sign in with your email to access your data rooms.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {status === 'sent' ? (
            <div className="flex flex-col items-center gap-4 py-4">
              <CheckCircle className="h-12 w-12 text-green-500" />
              <p className="text-center text-sm text-muted-foreground">
                Check your email for a magic link to sign in. The link expires
                in 1 hour.
              </p>
              <Button variant="outline" onClick={() => setStatus('idle')}>
                Use a different email
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium">
                  Email address
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button
                type="submit"
                className="w-full"
                disabled={status === 'loading'}
              >
                {status === 'loading' ? (
                  <span className="flex items-center gap-2">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    Sending...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Continue with email
                    <ArrowRight className="h-4 w-4" />
                  </span>
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
